import React, { createContext, useState, useContext, useEffect } from "react";

const STORAGE_KEY = 'casa_card_states';

const CardStateContext = createContext();

const loadPersistedState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        cardStates: parsed.cardStates || {},
        selectedState: parsed.selectedState || null,
      };
    }
  } catch (_) {}
  return { cardStates: {}, selectedState: null };
};

export const CardStateProvider = ({ children }) => {
  const persisted = loadPersistedState();

  const [cardStates, setCardStates] = useState(persisted.cardStates);
  const [selectedState, setSelectedState] = useState(persisted.selectedState);

  // Restore showNewsFlow from persisted cardStates (card index 0 = News)
  const [showNewsFlow, setShowNewsFlow] = useState(() => {
    const v = persisted.cardStates['0'];
    return v === 'expanded' || v === 'fullscreen';
  });

  // Persist to localStorage whenever cardStates or selectedState changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cardStates, selectedState }));
    } catch (_) {}
  }, [cardStates, selectedState]);

  const handleCardClick = (index) => {
    setCardStates((prev) => {
      const currentState = prev[index] || "inactive";
      let nextState;
      if (currentState === "inactive") {
        nextState = "expanded";
      } else if (currentState === "expanded") {
        nextState = "fullscreen";
      } else {
        nextState = "inactive";
      }

      const newStates = Object.keys(prev).reduce((acc, key) => {
        if (key === index.toString()) {
          acc[key] = prev[key] || "inactive";
        } else if (prev[key] === "fullscreen") {
          // A truly fullscreen card (e.g. a live call/stream) keeps running
          // as a smaller "expanded" tile in the grid instead of being
          // reset, so switching focus doesn't kill its session.
          acc[key] = "expanded";
        } else {
          // Any other card merely "expanded" (not fullscreen) has no live
          // session to preserve — left as "expanded" here, it and the
          // newly-activated card both get position:absolute + a z-index
          // from landing.css's ".expanded-col:nth-child(n)" rule, each its
          // own stacking context. Whichever is earlier in DOM order then
          // paints (and captures clicks) above the other's ENTIRE subtree
          // — including any of ITS OWN high z-index elements like a
          // dropdown menu — regardless of that subtree's own z-index,
          // since a descendant's z-index can never escape its ancestor's
          // stacking context. Resetting it to "inactive" here is what
          // actually fixed the buyer topbar's mobile dropdown being
          // unclickable while a sibling card (e.g. "Impact") was still
          // marked "expanded" from an earlier click.
          acc[key] = "inactive";
        }
        return acc;
      }, {});
      newStates[index] = nextState;

      if (index === 0) {
        setShowNewsFlow(nextState === "expanded" || nextState === "fullscreen");
      }
      return newStates;
    });
  };

  const handleClose = (index) => {
    setCardStates((prev) => {
      const currentState = prev[index];
      let newState;
      if (currentState === "fullscreen") {
        newState = "expanded";
      } else {
        newState = "inactive";
      }

      const newStates = { ...prev, [index]: newState };
      if (index === 0) {
        setShowNewsFlow(newState === "expanded" || newState === "fullscreen");
        if (newState === "inactive") {
          setSelectedState(null);
        }
      }
      return newStates;
    });
  };

  return (
    <CardStateContext.Provider
      value={{
        cardStates,
        handleCardClick,
        handleClose,
        showNewsFlow,
        setShowNewsFlow,
        selectedState,
        setSelectedState,
        setCardStates,
      }}
    >
      {children}
    </CardStateContext.Provider>
  );
};

export const useCardState = () => useContext(CardStateContext);
